import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alertwhale/ui'
import { Activity, Loader2, AlertCircle } from 'lucide-react'
import { useApplications } from '@/hooks/useApplications'
import { useCreateLiveSession } from '@/hooks/useLiveSessions'

interface CreateLiveSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateLiveSessionDialog({ open, onOpenChange }: CreateLiveSessionDialogProps) {
  const { data: applications, isLoading: appsLoading } = useApplications()
  const createSession = useCreateLiveSession()

  const [formData, setFormData] = useState({
    applicationId: '',
    title: '',
    subtitle: '',
    type: 'delivery',
    priority: 'default' as 'default' | 'high' | 'low',
    userId: '',
    referenceId: '',
  })
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.applicationId) {
      setError('Please select an application')
      return
    }
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }
    if (!formData.userId.trim()) {
      setError('User ID is required')
      return
    }

    try {
      await createSession.mutateAsync({
        application_id: formData.applicationId,
        audience: { user_id: formData.userId },
        type: formData.type,
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        reference_id: formData.referenceId || undefined,
        priority: formData.priority,
        progress: 0,
      })

      // Reset form and close dialog
      setFormData({
        applicationId: '',
        title: '',
        subtitle: '',
        type: 'delivery',
        priority: 'default',
        userId: '',
        referenceId: '',
      })
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create live session')
    }
  }

  const apps = applications || []

  const sessionTypes = [
    { value: 'delivery', label: 'Delivery' },
    { value: 'ride', label: 'Ride' },
    { value: 'order', label: 'Order' },
    { value: 'flight', label: 'Flight' },
    { value: 'workout', label: 'Workout' },
    { value: 'timer', label: 'Timer' },
    { value: 'progress', label: 'Progress' },
    { value: 'custom', label: 'Custom' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Create Live Session</DialogTitle>
          <DialogDescription>
            Start a real-time live activity for your users
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          {error && (
            <div className="flex items-center gap-3 rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="application">
              Application <span className="text-red-400">*</span>
            </Label>
            <Select
              value={formData.applicationId}
              onValueChange={(value) => setFormData({ ...formData, applicationId: value })}
            >
              <SelectTrigger id="application">
                <SelectValue placeholder={appsLoading ? 'Loading...' : 'Select an application'} />
              </SelectTrigger>
              <SelectContent>
                {apps.map((app) => (
                  <SelectItem key={app.id} value={app.id}>
                    {app.name}
                  </SelectItem>
                ))}
                {apps.length === 0 && !appsLoading && (
                  <div className="px-3 py-4 text-sm text-slate-500 text-center">
                    No applications found. Create one first.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="userId">
              User ID <span className="text-red-400">*</span>
            </Label>
            <Input
              id="userId"
              placeholder="Target user ID"
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-400">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Session title (e.g., Your order is on the way)"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subtitle">Subtitle</Label>
            <Input
              id="subtitle"
              placeholder="Additional info (optional)"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Session Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sessionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: 'default' | 'high' | 'low') =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referenceId">Reference ID</Label>
            <Input
              id="referenceId"
              placeholder="External reference (e.g., order_123)"
              value={formData.referenceId}
              onChange={(e) => setFormData({ ...formData, referenceId: e.target.value })}
            />
            <p className="text-xs text-slate-500">
              Use this to link the session to an external entity like an order or ride.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createSession.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createSession.isPending || apps.length === 0}
            >
              {createSession.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Activity className="h-4 w-4" />
                  Create Session
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
